import React, { useCallback, useState } from 'react';

type KeyCommandHandler = (command?: string) => 'handled' | 'not-handled';

export const KeyHandlerContext = React.createContext<{
    handlers: Record<string, [number, KeyCommandHandler][]>;
    register: (command: string, handler: KeyCommandHandler) => number;
    deregister: (command: string, handlerId?: number) => void;
    executeCommand: (command: string) => 'handled' | 'not-handled';
}>({
    handlers: {},
    register: () => {
        return 0;
    },
    deregister: () => {
        return;
    },
    executeCommand: () => 'not-handled',
});

export const KeyHandlerProvider: React.FC = ({ children }) => {
    const [handlers, setHandlers] = useState<Record<string, [number, KeyCommandHandler][]>>({});
    const [, setLastId] = useState<number>(0);

    const register = useCallback((command: string, handler: KeyCommandHandler) => {
        let id = 0;
        setLastId((last) => {
            id = last;
            return last + 1;
        });

        setHandlers((currentHandlers) => {
            return {
                ...currentHandlers,
                [command]: [...(currentHandlers[command] ?? []), [id, handler]],
            };
        });

        return id;
    }, []);

    const deregister = useCallback((command: string, handlerId?: number) => {
        setHandlers((currentHandlers) => {
            const commandHandlers = currentHandlers[command];
            if (commandHandlers) {
                const newHandlers = handlerId ? commandHandlers.filter((handler) => handler[0] !== handlerId) : [];
                return {
                    ...currentHandlers,
                    [command]: newHandlers,
                };
            }
            return currentHandlers;
        });
    }, []);

    const executeCommand = (command: string) => {
        const commandHandlers = handlers[command];
        if (!commandHandlers) return 'not-handled';

        const executionResults = commandHandlers.map((handler) => handler[1](command));

        return executionResults.every((result) => result === 'handled') ? 'handled' : 'not-handled';
    };

    return (
        <KeyHandlerContext.Provider value={{ handlers, register, deregister, executeCommand }}>
            {children}
        </KeyHandlerContext.Provider>
    );
};
